// X-4.4 · Recent searches storage hook.
//
// Persiste os últimos 10 termos de búsqueda únicos en localStorage para
// suxerirllos ao usuario na seguinte visita. Sólo gardamos string non
// baleiro tras `addSearch`. Garda a entrada máis recente PRIMEIRO.

import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "leyro_recent_searches";
const MAX_ENTRIES = 10;

function readFromStorage() {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((s) => typeof s === "string") : [];
  } catch {
    return [];
  }
}

function writeToStorage(list) {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    // Quota excedida ou navegador en modo privado — silencioso.
  }
}

export function useRecentSearches() {
  const [recent, setRecent] = useState(() => readFromStorage());

  // Sincronizar entre tabs (storage event).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onStorage = (ev) => {
      if (ev.key === STORAGE_KEY) setRecent(readFromStorage());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const add = useCallback((term) => {
    const t = (term || "").trim();
    if (!t) return;
    setRecent((prev) => {
      // Move o termo ao top (deduplica case-insensitive).
      const filtered = prev.filter((s) => s.toLowerCase() !== t.toLowerCase());
      const next = [t, ...filtered].slice(0, MAX_ENTRIES);
      writeToStorage(next);
      return next;
    });
  }, []);

  const remove = useCallback((term) => {
    setRecent((prev) => {
      const next = prev.filter((s) => s !== term);
      writeToStorage(next);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    writeToStorage([]);
    setRecent([]);
  }, []);

  return { recent, add, remove, clear };
}
